import { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { api, type Comment } from '../api';
import { Markdown } from '../components/Markdown';

/**
 * One comment, as a card with a shaded header.
 *
 * A comment is often long, and an agent's carries its own headings — so the boundary
 * between two of them has to be stronger than the text inside either. The border draws
 * where the comment ends; the header band draws where the next one starts, and holds the
 * one thing that says whose it is.
 *
 * `article` is not decoration either: it gives each comment its own node in the
 * accessibility tree, which is how a test — and a screen reader — counts them.
 */
function CommentRow({ comment }: { comment: Comment }) {
  return (
    <Paper variant="outlined" component="article" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        flexWrap="wrap"
        sx={{ px: 1.5, py: 0.75, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="body2" fontWeight={600}>
          {comment.author}
        </Typography>
        {/* An agent's comment is chipped, the same as in the activity log — reading a
            board where you cannot tell who wrote what is how you stop trusting it. */}
        {comment.authorKind === 'token' && <Chip size="small" label="agent" color="primary" />}
        <Typography variant="caption" color="text.secondary">
          {new Date(comment.at).toLocaleString()}
        </Typography>
      </Stack>
      {/* Little vertical padding of its own: the markdown's first and last blocks bring
          their own margins, and doubling up sits the text low in the card. */}
      <Box sx={{ px: 1.5, py: 0.5 }}>
        {/* Typed in a plain textarea, so a newline is a newline — see `hardBreaks`. */}
        <Markdown hardBreaks>{comment.text}</Markdown>
      </Box>
    </Paper>
  );
}

/**
 * The composer owns the draft, and that is the point of it being its own component.
 * react-markdown's synchronous component memoizes nothing — it builds a processor and
 * re-parses on every render — so a draft held one level up would re-parse every comment on
 * the ticket per keystroke. Keeping the state here means the list is not re-rendered at
 * all while you type, structurally rather than by a `memo` someone can lift off.
 */
function CommentComposer({
  ticketRef,
  onAdded,
}: {
  ticketRef: number;
  onAdded: (comment: Comment) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      const result = await api.comment(ticketRef, text);
      onAdded(result.comment);
      setText(''); // Cleared only on success — a failed post keeps what you typed.
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Could not add the comment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {problem && (
        <Alert severity="error" onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      )}

      <TextField
        size="small"
        multiline
        minRows={2}
        placeholder="Add a comment"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <Box>
        <Button size="small" variant="outlined" disabled={busy || !text.trim()} onClick={() => void submit()}>
          {busy ? 'Posting…' : 'Comment'}
        </Button>
      </Box>
    </>
  );
}

export function Comments({
  ticketRef,
  comments,
  onAdded,
}: {
  ticketRef: number;
  comments: Comment[];
  onAdded: (comment: Comment) => void;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="overline" color="text.secondary">
        Comments {comments.length > 0 && `(${comments.length})`}
      </Typography>

      {comments.map((comment) => (
        <CommentRow key={comment.id} comment={comment} />
      ))}

      <CommentComposer ticketRef={ticketRef} onAdded={onAdded} />
    </Stack>
  );
}
